// Fetcht de meetstandaard-catalogus en extraheert de unieke set van sector-slugs
// die daarin voorkomen.
//
// Gebruikt door Step 3 (sector-picker) zodat de lijst meegroeit met de api —
// niet hardcoded in de frontend.
//
// Returnt:
//   {
//     sectors: string[],            // precies wat de api publiceert, uit BEIDE pipelines
//     selectableSectors: string[],  // unie met sectorCatalog — voor pickers
//     loading: boolean,
//     error: string | null
//   }
//
// Er draaien twee api-pipelines tegelijk: legacy `/database` (arbeidsparticipatie,
// gelijke-kansen) en de versioned documenten (energiearmoede, rest volgt).
// Energiearmoede komt nooit in `/database` (inbox #96). Deze hook las vroeger
// alleen `/database`, waardoor `sectors` stelselmatig een hele standaard miste —
// een lijst die er compleet uitzag en het niet was. Beide pipelines lopen nu via
// `fetchStandaardCatalogs`, dus `sectors` is weer waar wat de naam belooft.
//
// Waarom er tóch twee lijsten zijn: de api kan achterlopen op onze eigen
// sectoren. `selectableSectors` = "wat mag de gebruiker kiezen" (unie met onze
// eigen catalogus, blijft gevuld als de fetch faalt); `sectors` = "waar heeft de
// api daadwerkelijk effecten voor" (leeg bij een fout — een lege lijst is
// zichtbaar, een half gevulde niet).
//
// AI-CONTEXT: See docs/pitfalls.md#api-dual-pipeline-legacy-only-consumer

import { useEffect, useMemo, useState } from 'react'
import { SECTOR_INFO } from '../helpers/meetstandaard/sectorCatalog'
import fetchStandaardCatalogs from '../helpers/meetstandaard/fetchStandaardCatalogs'
import mergeStandaardCatalogs from '../helpers/meetstandaard/mergeStandaardCatalogs'

const useAvailableSectors = () => {

    const [sectors, setSectors] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {

        let cancelled = false

        fetchStandaardCatalogs()
            .then(({ legacy, documents }) => {

                if (cancelled) return

                const set = new Set()
                mergeStandaardCatalogs(legacy, documents).forEach((category) => {
                    const effects = Array.isArray(category?.effects) ? category.effects : []
                    effects.forEach((effect) => {
                        const effectSectors = Array.isArray(effect?.sectors) ? effect.sectors : []
                        effectSectors.forEach((slug) => {
                            if (slug && typeof slug === 'string') set.add(slug)
                        })
                    })
                })

                setSectors([...set].sort())
                setLoading(false)
            })
            .catch((err) => {
                if (cancelled) return
                console.error('useAvailableSectors fetch error', err)
                setError(err.message || 'Kon meetstandaard niet ophalen')
                setLoading(false)
            })

        return () => { cancelled = true }

    }, [])

    // Unie van wat de api publiceert en wat wij lokaal al kennen. Blijft gevuld
    // als de fetch faalt — een lege picker is erger dan een picker met de
    // sectoren waar we sowieso labels voor hebben.
    const selectableSectors = useMemo(
        () => [...new Set([...sectors, ...Object.keys(SECTOR_INFO)])].sort(),
        [sectors]
    )

    return { sectors, selectableSectors, loading, error }

}

export default useAvailableSectors
